import { ComboInputObject } from 'types/formElement';
import { alphaNumericNonSpecial, emailRegex /* , pecRegex */ } from './regex';
import { CHECK_CODE_CHARS, CHECK_CODE_EVEN, CHECK_CODE_ODD } from './validators/taxCodeId';
import { isValid as checkValidIban } from 'iban';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { euCountries, prefixes } from './models/DataSelect';
import { verifyFiscalCode, verifyPec, verifyVat } from '@api/data';
import ReCAPTCHA from 'react-google-recaptcha';
import { DEFAULT_EXTRA_UE_VAT, VAT_VALUE_EXCEPTION } from './constants';
import { AccountInfo } from '@azure/msal-browser';
import { ApplicantInfo } from 'types/applicant';

export const toCamelCase = (string: string): string => {
    const ans = string.toLowerCase();

    // Returning string to camelcase
    return ans.split(' ').reduce((s, c) => s + (c.charAt(0).toUpperCase() + c.slice(1)));
};

export const validateVat = async (pIva: string, countryCode: string, recaptcha: ReCAPTCHA) => {
    if (pIva === '') return '';
    if (!countryCode) {
        return 'Completa il campo Nazione prima di proseguire.';
    }
    if (pIva === '00000000000') {
        return 'La partita IVA non è valida.';
    }
    if (countryCode === 'IT' || countryCode === 'it') {
        if (pIva.length !== 11)
            return 'La lunghezza della partita IVA non è corretta: la partita IVA dovrebbe essere lunga esattamente 11 caratteri.';
        const validi = '0123456789';
        for (let i = 0; i < 11; i++) {
            if (validi.indexOf(pIva.charAt(i)) === -1)
                return `La partita IVA contiene un carattere non valido: '${pIva.charAt(
                    i,
                )}'. I caratteri validi sono le cifre`;
        }
        let s = 0;
        for (let i = 0; i <= 9; i += 2) s += pIva.charCodeAt(i) - '0'.charCodeAt(0);
        for (let i = 1; i <= 9; i += 2) {
            let c = 2 * (pIva.charCodeAt(i) - '0'.charCodeAt(0));
            if (c > 9) c = c - 9;
            s += c;
        }
        if ((10 - (s % 10)) % 10 !== pIva.charCodeAt(10) - '0'.charCodeAt(0))
            return 'La partita IVA non è valida: il codice di controllo non corrisponde.';
        const vatOk = await verifyVat(pIva, countryCode, recaptcha);
        if (!vatOk) {
            return 'La partita IVA non è valida.';
        }
        if (vatOk === VAT_VALUE_EXCEPTION) {
            return VAT_VALUE_EXCEPTION;
        }
        return true;
    } else {
        const countryInUE = euCountries.find((country) => country.id === countryCode);
        if (pIva === DEFAULT_EXTRA_UE_VAT || !countryInUE) {
            return true;
        }
        const vatOk = await verifyVat(pIva, countryCode, recaptcha);

        if (!vatOk) {
            return 'La partita IVA non è valida.';
        }
        if (vatOk === VAT_VALUE_EXCEPTION) {
            return VAT_VALUE_EXCEPTION;
        }
        return true;
    }
};

export const validateTaxIdCode = async (
    code: string,
    countryCode: string,
    recaptcha: ReCAPTCHA,
    specialCustomsArrangements = false,
    isRequired = true,
) => {
    if (code.length === 11) {
        if (specialCustomsArrangements) {
            return true;
        }
        if (code === '00000000000') {
            return 'Codice fiscale non corretto';
        }
        const isValidVat = await validateVat(code, countryCode, recaptcha);
        if (!isValidVat) {
            return isValidVat;
        }
        return true;
    }
    if (typeof code !== 'string') {
        return 'Codice fiscale non corretto';
    }
    let cf = code.toUpperCase();
    if (cf.length !== 16) {
        return 'Il codice fiscale deve avere almeno 16 caratteri';
    }
    if (!/^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1}$/.test(cf)) {
        return 'Il codice fiscale non è corretto';
    }
    const expectedCheckCode = code.charAt(15);
    cf = code.slice(0, 15);
    cf = cf.toUpperCase();
    let val = 0;
    for (let i = 0; i < 15; i = i + 1) {
        const c = cf[i];
        val += i % 2 !== 0 ? CHECK_CODE_EVEN[c] : CHECK_CODE_ODD[c];
    }
    val = val % 26;
    const checkedCode = CHECK_CODE_CHARS.charAt(val);

    if (checkedCode.toUpperCase() !== expectedCheckCode.toUpperCase()) {
        return 'Il codice fiscale non è corretto. Controlla i dati inseriti.';
    }
    if (isRequired && !specialCustomsArrangements) {
        const fiscalCodeOk = await verifyFiscalCode(code, recaptcha);
        if (!fiscalCodeOk) {
            return 'Il codice fiscale non è corretto. Controlla i dati inseriti.';
        }
    }
    return true;
};

export const validateIban = (iban: string): string | boolean => {
    if (checkValidIban(iban)) {
        return true;
    } else {
        return `L'IBAN inserito non è corretto.`;
    }
};

export const validateEmail = (email: string): string | boolean => {
    if (emailRegex.test(email)) {
        return true;
    } else {
        return "L'email inserita non è corretta.";
    }
};

export const defaultValidationNoRequiredInput = (): boolean => {
    return true;
};

export const validateNonSpecialString = (text: string): string | boolean => {
    if (alphaNumericNonSpecial.test(text)) {
        return true;
    } else {
        return 'La stringa inserita non è corretta.';
    }
};

export const validatePec = async (pec: string, recaptcha: ReCAPTCHA) => {
    if (pec.trim() === '' || emailRegex.test(pec)) {
        const pecOk = await verifyPec(pec.split('@')[1], recaptcha);
        if (!pecOk) {
            return 'La PEC inserita non è corretta.';
        }
        return true;
    } else {
        return 'La PEC inserita non è corretta.';
    }
};

export const validatePhone = (value: ComboInputObject): string | boolean => {
    if (value.selectValue && value.inputValue) {
        const foundPrefix = prefixes.find((prefix) => prefix.id === value.selectValue.toString());
        if (foundPrefix) {
            const number = `${foundPrefix.label} ${value.inputValue}`;
            if (isValidPhoneNumber(number, foundPrefix.id as CountryCode)) {
                return true;
            } else {
                return 'Numero di telefono non valido';
            }
        }
    }
    if (value.isRequired && !value.selectValue) {
        return 'Seleziona un valore dalla lista.';
    }
    if (value.isRequired && !value.inputValue) {
        return 'Inserisci un numero di telefono.';
    }
    return true;
};

export const validateSdi = (value: string, isPa: boolean) => {
    if (value) {
        if (isPa) {
            if (value.length === 6) {
                return true;
            } else {
                return 'Inserisci un codice SDI valido.';
            }
        } else {
            if (value.length === 7) {
                return true;
            } else {
                return 'Inserisci un codice SDI valido.';
            }
        }
    }
    return 'Inserisci un codice SDI valido.';
};

// Derives applicant name/surname/email from the Entra ID token claims already cached by MSAL
// (login scopes include `profile email`) — no extra backend call needed. given_name/family_name
// are not part of msal-browser's typed TokenClaims but are standard OIDC claims returned by
// Entra, hence the index-signature cast. Falls back to splitting the full `name` claim at the
// last space (surname is usually the last token, e.g. "Mario Rossi Bianchi" -> "Mario Rossi" /
// "Bianchi"), then to `account.username` (preferred_username/UPN) for the email if the `email`
// claim is absent.
export const getApplicantInfoFromAccount = (account?: AccountInfo | null): ApplicantInfo => {
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const nameParts = (account?.name ?? '').trim().split(/\s+/);
    const fallbackFamilyName = nameParts.length > 1 ? nameParts.pop() : '';
    const fallbackGivenName = nameParts.join(' ');

    const givenName = (claims?.given_name as string) || fallbackGivenName;
    const familyName = (claims?.family_name as string) || fallbackFamilyName || '';
    const email = (claims?.email as string) || account?.username || '';

    return {
        applicantName: givenName,
        applicantSurname: familyName,
        applicantEmail: email,
    };
};

export const fileToBase64 = (file: File) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(reader.result);
        };
    });
};
